export type ThemeName = 'light' | 'dark';

// Figma Design System Color Tokens
export type UIColorTokens = {
    // Brand Colors
    bloomRed: string;
    bloomRedDark: string;

    // Font Colors
    fontPrimary: string;
    fontSecondary: string;
    fontTertiary: string;
    fontHighContrast: string;
    fontErrorText: string;
    fontSuccessText: string;
    /** Muted label (e.g. handover) — Figma #6E6E6E */
    fontMuted: string;
    /** Welcome web CTA copy, footer text, links, and social icons — #1f1919 */
    welcomeInk: string;

    // Background Colors
    backgroundLevel1: string; // Main background
    backgroundLevel2: string; // Secondary background
    backgroundLevel3: string; // Elevated surfaces
    backgroundLevel4: string; // Disabled/inactive
    backgroundSplash: string; // Splash screen background

    // Outline Colors
    outlineDisabled: string;
    outlineInactive: string;
    outlineActive: string;
    outlineError: string;
    outlineSuccess: string;

    // Button Colors
    buttonDefault: string;
    buttonPressed: string;
    buttonDisabled: string;

    // Vector/Icon Colors
    vectorsLogo: string;
    vectorsIconBrand: string;
    vectorsIconGreyscale: string;

    // Special Colors
    white: string;

    // Carousel & Badge Colors
    carouselDotActive: string;
    carouselDotInactive: string;
    carouselBadgeBg: string;
    /** Guest listing card — units-count badge over the carousel image (Figma #E7E7E7) */
    unitsBadgeBg: string;
    /** Guest listing card — property type tag fill, e.g. "Villa" (Figma #F3F3F3) */
    propertyTagBg: string;

    // Link/Action Colors
    linkBlue: string;

    appointmentIconBg: string;
    appointmentIconTint: string;
    appointmentBadgeBg: string;
    appointmentBadgeText: string;
    reserveIconBg: string;
    reserveIconTint: string;
    skeletonBaseDark: string;
    skeletonHighlightDark: string;
    /** Listing chip — Pending (text #E64A19 / fill #FFCCBC99) */
    reservationPendingBg: string;
    reservationPendingText: string;
    /** Listing chip — In Progress fill #FEF8C4 */
    reservationInProgressBg: string;
    /** Unit details + listing chip — In Progress text #BF8C08 */
    reservationInProgressText: string;
    /** Listing chip — Reservation Confirmed (text #1A5E20 / fill #C8E6C9) */
    reservationListingConfirmedBg: string;
    reservationListingConfirmedText: string;
    /** Unit details — Confirmed clock/message */
    reservationConfirmedText: string;
    /** Owned ADHA unit status chip on the property listing card */
    adhaUnitStatusText: string;
    adhaUnitStatusBg: string;

    /** Confirm reservation — Iskan loan amount / label */
    iskanLoanText: string;
    /** Confirm reservation — remaining balance amount / label */
    remainingBalanceText: string;
    /** Confirm reservation — “Important to Note” callout */
    importantNoteBg: string;
    importantNoteBorder: string;
    importantNoteTitle: string;
    /** Confirm reservation — Important Note body — Figma #45464D */
    importantNoteBody: string;
    /** Confirm reservation — “What happens next?” border — Figma #CCCCCC */
    confirmNextBorder: string;
    /** Reserve Unit — success status banner ("Reserved! Awaiting your Signature") */
    successNoteBg: string;
    successNoteBorder: string;
    successNoteTitle: string;

    // Modal Backdrop Overlay
    modalBackdrop: string;

    // Bloom Experience hero landing screen (Figma exact values, fixed
    // regardless of theme — this screen has its own dark photo backdrop)
    bloomExperienceLogoText: string;
    bloomExperienceLabelText: string;
    bloomExperienceTitleText: string;

    /** Bloom Universal hub + space — Figma values, fixed in both themes. */
    bloomUniversalPage: string;
    bloomUniversalInk: string;
    bloomUniversalInkStrong: string;
    bloomUniversalOnPhoto: string;
    bloomUniversalMuted: string;
    bloomUniversalEyebrow: string;
    bloomUniversalCream: string;
    bloomUniversalCardSubtitle: string;
    bloomUniversalImageFallback: string;
    bloomUniversalDetailScrim: string;
    bloomUniversalCardScrim: string;
    bloomUniversalHeroScrimCenter: string;
    bloomUniversalHeroScrimEdge: string;
    bloomUniversalCallShadow: string;
    bloomUniversalCardBorder: string;
    bloomUniversalDetailBorder: string;
    bloomUniversalInsetHighlight: string;
    bloomUniversalIconBadge: string;
    bloomUniversalActionShadow: string;
    bloomUniversalDetailShadow: string;
    bloomUniversalActionScrimStart: string;
    bloomUniversalActionScrimEnd: string;
    bloomUniversalDetailScrimStart: string;
    bloomUniversalDetailScrimEnd: string;
    bloomUniversalPageOverlay: string;
    bloomUniversalVignetteTop: string;
    bloomUniversalVignetteMid: string;
    bloomUniversalVignetteBottom: string;
    bloomUniversalCallLiftShadow: string;
};

// Internal Figma Primitive Colors (kept private; not part of UIColorTokens)
type UIPrimitiveColors = {
    redLightest2: string;
    /** Listing in-progress chip fill (Figma #FEF8C4) */
    yellowLightest: string;
    /** Listing confirmed chip fill (Figma #C8E6C9) */
    greenLighter: string;
    lightGrey: string;
    lighterGrey: string;
    redLight: string;
    redDefault: string;
    yellowLighter: string;
    yellowDark: string;
    greenDark: string;
    greenNormal: string;
    greenMedium: string;
    /** Figma Iskan loan amount green */
    greenIskan: string;
    orangeNormal: string;
    /** Figma “Important to Note” title */
    orangeDeep: string;
    /** Figma reserved “Pending” badge fill (peach @ 60%) */
    orangePale60: string;
    /** Owned ADHA unit listing chip — text #EC4D1B / fill #FDE5CF */
    adhaUnitStatusText: string;
    adhaUnitStatusBg: string;
    /** Reservation in-progress status (sheet #BF8C08) */
    goldInProgress: string;
    creamLighter: string;
    /** Figma important-note callout fill */
    creamWarmBg: string;
    /** Figma important-note callout border */
    creamWarmBorder: string;
    white60: string;
    /** Figma muted grey (handover, etc.) */
    greyMuted: string;
    /** Figma Important Note body text */
    grey45464D: string;
    /** Figma confirm-next section border (#CCCCCC) */
    greyCCCC: string;
    /** Figma disabled control fill (e.g. date nav arrows) */
    greyEEEE: string;
    /** Figma warm grey placeholder (units filter search) */
    greyWarm625C59: string;
    /** Figma selected-chip fill (units filter bedrooms) */
    redPaleF5EAE8: string;
    /** Figma selected-row fill (units listing bedroom dropdown) */
    redPaleFBF3F1: string;
    /** Figma ink text (units listing filter labels / search text) */
    inkWarm201D1C: string;
    /** Welcome web CTA and footer ink */
    welcomeInk: string;
    /** Figma slate border (date/time chips) */
    borderSlate: string;
    bluePrimary: string;
    /** UAE PASS button guidelines: title/fill must be exactly black or white */
    uaePassBlack: string;
    uaePassWhite: string;
    // Modal/overlay backdrop color (centralized primitive)
    overlayBackdrop: string;
    bloomRedDark: string;
    // Bloom Experience hero landing screen (Figma exact values)
    bloomExperienceCream: string;
    bloomExperienceGold: string;
    bloomExperienceIvory: string;
    bloomUniversalPage: string;
    bloomUniversalInk: string;
    bloomUniversalInkStrong: string;
    bloomUniversalOnPhoto: string;
    bloomUniversalMuted: string;
    bloomUniversalEyebrow: string;
    bloomUniversalCardSubtitle: string;
    bloomUniversalImageFallback: string;
    bloomUniversalDetailScrim: string;
    bloomUniversalCardScrim: string;
    bloomUniversalHeroScrimCenter: string;
    bloomUniversalHeroScrimEdge: string;
    bloomUniversalCallShadow: string;
    bloomUniversalCardBorder: string;
    bloomUniversalDetailBorder: string;
    bloomUniversalInsetHighlight: string;
    bloomUniversalIconBadge: string;
    bloomUniversalActionShadow: string;
    bloomUniversalDetailShadow: string;
    bloomUniversalActionScrimStart: string;
    bloomUniversalActionScrimEnd: string;
    bloomUniversalDetailScrimStart: string;
    bloomUniversalDetailScrimEnd: string;
    bloomUniversalPageOverlay: string;
    bloomUniversalVignetteTop: string;
    bloomUniversalVignetteMid: string;
    bloomUniversalVignetteBottom: string;
    bloomUniversalCallLiftShadow: string;
};

export const UI_PRIMITIVES: UIPrimitiveColors = {
    bloomRedDark: '#8F2B23',
    redLightest2: '#fecdd2',
    yellowLightest: '#FEF8C4',
    greenLighter: '#C8E6C9',
    lightGrey: '#BFBFBF',
    lighterGrey: '#D9D9D9',
    redLight: '#922A22', //  changed from '#D8181E' to '#922A22' as per marketing feedback
    redDefault: '#D8181E',
    yellowLighter: '#FEF076',
    yellowDark: '#EFBC67', //  changed from '#FAC12D' to '#EFBC67' as per marketing feedback
    greenDark: '#5C7F71', //  changed from '#388E3C' to '#5C7F71' as per marketing feedback
    greenNormal: '#4CAF50',
    greenMedium: '#388E3C',
    greenIskan: '#1A5E20',
    orangeNormal: '#FF5722',
    orangeDeep: '#E64A19',
    orangePale60: '#FFCCBC99',
    adhaUnitStatusText: '#EC4D1B',
    adhaUnitStatusBg: '#FDE5CF',
    goldInProgress: '#BF8C08',
    creamLighter: '#F8EAD5',
    creamWarmBg: '#FFF7F0',
    creamWarmBorder: '#FFE3CF',
    white60: '#999999',
    greyMuted: '#6E6E6E',
    grey45464D: '#45464D',
    greyCCCC: '#CCCCCC',
    greyEEEE: '#EEEEEE',
    greyWarm625C59: '#625C59',
    redPaleF5EAE8: '#F5EAE8',
    redPaleFBF3F1: '#FBF3F1',
    inkWarm201D1C: '#201D1C',
    welcomeInk: '#1f1919',
    borderSlate: '#E2E8F0',
    bluePrimary: '#5095EF',
    uaePassBlack: '#000000',
    uaePassWhite: '#FFFFFF',
    // 50% black per Figma modal specs
    overlayBackdrop: 'rgba(0, 0, 0, 0.5)',
    // Bloom Experience hero landing screen (Figma exact values)
    bloomExperienceCream: '#F0EAD8',
    bloomExperienceGold: '#E0C397',
    bloomExperienceIvory: '#F8F5EB',
    bloomUniversalPage: '#FFFFFF',
    bloomUniversalInk: '#333333',
    bloomUniversalInkStrong: '#1A1A1A',
    bloomUniversalOnPhoto: '#FFFFFF',
    bloomUniversalMuted: '#E3E3E3',
    bloomUniversalEyebrow: '#000000',
    bloomUniversalCardSubtitle: '#F3F1EA',
    bloomUniversalImageFallback: '#2A2A2A',
    bloomUniversalDetailScrim: '#0C0A0B',
    bloomUniversalCardScrim: '#1C1C19',
    bloomUniversalHeroScrimCenter: 'rgba(49, 49, 49, 0.28)',
    bloomUniversalHeroScrimEdge: 'rgba(0, 0, 0, 0.75)',
    bloomUniversalCallShadow: 'rgba(0, 0, 0, 0.149)',
    bloomUniversalCardBorder: 'rgba(255, 255, 255, 0.15)',
    bloomUniversalDetailBorder: 'rgba(255, 255, 255, 0.12)',
    bloomUniversalInsetHighlight: 'rgba(255, 255, 255, 0.08)',
    bloomUniversalIconBadge: 'rgba(255, 255, 255, 0.18)',
    bloomUniversalActionShadow: 'rgba(0, 0, 0, 0.25)',
    bloomUniversalDetailShadow: 'rgba(0, 0, 0, 0.4)',
    bloomUniversalActionScrimStart: 'rgba(28, 28, 25, 0)',
    bloomUniversalActionScrimEnd: 'rgba(28, 28, 25, 0.85)',
    bloomUniversalDetailScrimStart: 'rgba(12, 10, 11, 0)',
    bloomUniversalDetailScrimEnd: 'rgba(12, 10, 11, 0.55)',
    bloomUniversalPageOverlay: 'rgba(0, 0, 0, 0.48)',
    bloomUniversalVignetteTop: 'rgba(0, 0, 0, 0.42)',
    bloomUniversalVignetteMid: 'rgba(0, 0, 0, 0.5)',
    bloomUniversalVignetteBottom: 'rgba(0, 0, 0, 0.62)',
    bloomUniversalCallLiftShadow: 'rgba(0, 0, 0, 0.18)',
};

// Figma Light Theme Colors (exact from design system)
export const UI_COLORS: Record<ThemeName, Partial<UIColorTokens>> = {
    light: {
        // Brand Colors
        bloomRed: '#b61d22',
        bloomRedDark: UI_PRIMITIVES.bloomRedDark,

        // Font Colors
        fontPrimary: '#333333',
        fontSecondary: '#4d4d4d',
        fontTertiary: '#666666',
        fontMuted: UI_PRIMITIVES.greyMuted,
        welcomeInk: UI_PRIMITIVES.welcomeInk,
        fontHighContrast: '#1a1a1a',
        fontErrorText: UI_PRIMITIVES.redLight, // Updated from Figma
        fontSuccessText: UI_PRIMITIVES.greenDark, // Updated from Figma

        // Background Colors
        backgroundLevel1: '#FFFFFF',  // Updated from Figma
        backgroundLevel2: '#F5F5F5',  // Updated from Figma
        backgroundLevel3: '#E3E3E3',  // Updated from Figma
        backgroundLevel4: '#D6D6D6',  // Updated from Figma
        backgroundSplash: '#F7F3E9',

        // Outline Colors
        outlineDisabled: '#D6D6D6', // Updated from Figma
        outlineInactive: '#E3E3E3', // Figma Outline/Inactive
        outlineActive: '#EDEDED',   // Updated from Figma
        outlineError: '#F00008',    // Updated from Figma
        outlineSuccess: '#4CAF50',  // Updated from Figma

        // Button Colors (from Figma tokens)
        buttonDefault: '#1a1a1a',   // White/10 - dark button
        buttonPressed: '#4d4d4d',   // White/30 - darker when pressed
        buttonDisabled: '#B2B2B2',  // White/70 - lighter gray when disabled

        // Vector/Icon Colors
        vectorsLogo: '#b61d22',
        vectorsIconBrand: '#b61d22',
        vectorsIconGreyscale: '#333333',

        // Special Colors
        white: '#ffffff',

        // Carousel & Badge Colors
        carouselDotActive: '#ffffff',
        carouselDotInactive: 'rgba(255, 255, 255, 0.4)',
        carouselBadgeBg: 'rgba(255, 255, 255, 0.85)',
        unitsBadgeBg: '#E7E7E7',
        propertyTagBg: '#F3F3F3',

        // Link/Action Colors
        linkBlue: '#0059B2',

        // Next Step / Action Card Colors
        appointmentIconBg: '#FDF2E9',
        appointmentIconTint: '#E65F2B',
        appointmentBadgeBg: UI_PRIMITIVES.redLightest2,
        appointmentBadgeText: UI_PRIMITIVES.redLight,
        reserveIconBg: '#E8F5E9',
        reserveIconTint: '#2E7D32',
        skeletonBaseDark: '#E0E0E0',
        skeletonHighlightDark: '#C0C0C0',
        reservationPendingBg: UI_PRIMITIVES.orangePale60,
        reservationPendingText: UI_PRIMITIVES.orangeDeep,
        reservationInProgressBg: UI_PRIMITIVES.yellowLightest,
        reservationInProgressText: UI_PRIMITIVES.goldInProgress,
        reservationListingConfirmedBg: UI_PRIMITIVES.greenLighter,
        reservationListingConfirmedText: UI_PRIMITIVES.greenIskan,
        reservationConfirmedText: UI_PRIMITIVES.greenMedium,
        adhaUnitStatusText: UI_PRIMITIVES.adhaUnitStatusText,
        adhaUnitStatusBg: UI_PRIMITIVES.adhaUnitStatusBg,

        iskanLoanText: UI_PRIMITIVES.greenMedium,
        remainingBalanceText: UI_PRIMITIVES.redDefault,
        importantNoteBg: UI_PRIMITIVES.creamWarmBg,
        importantNoteBorder: UI_PRIMITIVES.creamWarmBorder,
        importantNoteTitle: UI_PRIMITIVES.orangeDeep,
        importantNoteBody: UI_PRIMITIVES.grey45464D,
        confirmNextBorder: UI_PRIMITIVES.greyCCCC,
        successNoteBg: '#E8F5E9',
        successNoteBorder: UI_PRIMITIVES.greenIskan,
        successNoteTitle: UI_PRIMITIVES.greenIskan,

        // Modal Backdrop Overlay
        modalBackdrop: 'rgba(0, 0, 0, 0.4)',

        // Bloom Experience hero landing screen
        bloomExperienceLogoText: UI_PRIMITIVES.bloomExperienceCream,
        bloomExperienceLabelText: UI_PRIMITIVES.bloomExperienceGold,
        bloomExperienceTitleText: UI_PRIMITIVES.bloomExperienceIvory,

        bloomUniversalPage: UI_PRIMITIVES.bloomUniversalPage,
        bloomUniversalInk: UI_PRIMITIVES.bloomUniversalInk,
        bloomUniversalInkStrong: UI_PRIMITIVES.bloomUniversalInkStrong,
        bloomUniversalOnPhoto: UI_PRIMITIVES.bloomUniversalOnPhoto,
        bloomUniversalMuted: UI_PRIMITIVES.bloomUniversalMuted,
        bloomUniversalEyebrow: UI_PRIMITIVES.bloomUniversalEyebrow,
        bloomUniversalCream: UI_PRIMITIVES.bloomExperienceCream,
        bloomUniversalCardSubtitle: UI_PRIMITIVES.bloomUniversalCardSubtitle,
        bloomUniversalImageFallback: UI_PRIMITIVES.bloomUniversalImageFallback,
        bloomUniversalDetailScrim: UI_PRIMITIVES.bloomUniversalDetailScrim,
        bloomUniversalCardScrim: UI_PRIMITIVES.bloomUniversalCardScrim,
        bloomUniversalHeroScrimCenter: UI_PRIMITIVES.bloomUniversalHeroScrimCenter,
        bloomUniversalHeroScrimEdge: UI_PRIMITIVES.bloomUniversalHeroScrimEdge,
        bloomUniversalCallShadow: UI_PRIMITIVES.bloomUniversalCallShadow,
        bloomUniversalCardBorder: UI_PRIMITIVES.bloomUniversalCardBorder,
        bloomUniversalDetailBorder: UI_PRIMITIVES.bloomUniversalDetailBorder,
        bloomUniversalInsetHighlight: UI_PRIMITIVES.bloomUniversalInsetHighlight,
        bloomUniversalIconBadge: UI_PRIMITIVES.bloomUniversalIconBadge,
        bloomUniversalActionShadow: UI_PRIMITIVES.bloomUniversalActionShadow,
        bloomUniversalDetailShadow: UI_PRIMITIVES.bloomUniversalDetailShadow,
        bloomUniversalActionScrimStart: UI_PRIMITIVES.bloomUniversalActionScrimStart,
        bloomUniversalActionScrimEnd: UI_PRIMITIVES.bloomUniversalActionScrimEnd,
        bloomUniversalDetailScrimStart: UI_PRIMITIVES.bloomUniversalDetailScrimStart,
        bloomUniversalDetailScrimEnd: UI_PRIMITIVES.bloomUniversalDetailScrimEnd,
        bloomUniversalPageOverlay: UI_PRIMITIVES.bloomUniversalPageOverlay,
        bloomUniversalVignetteTop: UI_PRIMITIVES.bloomUniversalVignetteTop,
        bloomUniversalVignetteMid: UI_PRIMITIVES.bloomUniversalVignetteMid,
        bloomUniversalVignetteBottom: UI_PRIMITIVES.bloomUniversalVignetteBottom,
        bloomUniversalCallLiftShadow: UI_PRIMITIVES.bloomUniversalCallLiftShadow,
    },
    dark: {
        // Brand Colors (keep same for consistency)
        bloomRed: '#b61d22', // Keep brand red consistent
        bloomRedDark: UI_PRIMITIVES.bloomRedDark,

        // Font Colors (inverted and adjusted for dark mode)
        fontPrimary: '#ffffff',
        fontSecondary: '#cccccc',
        fontTertiary: '#999999',
        fontMuted: '#999999',
        fontHighContrast: '#ffffff',
        fontErrorText: UI_PRIMITIVES.redLight,
        fontSuccessText: UI_PRIMITIVES.greenNormal,

        // Background Colors (dark mode hierarchy)
        backgroundLevel1: '#1a1a1a',  // Dark background
        backgroundLevel2: '#2d2d2d',  // Secondary background
        backgroundLevel3: '#404040',  // Elevated surfaces
        backgroundLevel4: '#666666',  // Disabled/inactive
        backgroundSplash: '#1a1a1a',

        // Outline Colors (adjusted for dark mode)
        outlineDisabled: '#404040',
        outlineInactive: '#666666',
        outlineActive: '#999999',
        outlineError: '#ff5252',
        outlineSuccess: '#4caf50',

        // Button Colors (from Figma tokens - dark mode)
        buttonDefault: '#ededed',   // White/93 - light button
        buttonPressed: '#ffffff',   // White/100 - white when pressed
        buttonDisabled: '#4d4d4d',  // White/30 - darker gray when disabled

        // Vector/Icon Colors (adjusted for dark mode)
        vectorsLogo: '#b61d22',
        vectorsIconBrand: '#b61d22',
        vectorsIconGreyscale: '#cccccc',

        // Special Colors
        white: '#ffffff',

        // Carousel & Badge Colors
        carouselDotActive: '#ffffff',
        carouselDotInactive: 'rgba(255, 255, 255, 0.4)',
        carouselBadgeBg: 'rgba(255, 255, 255, 0.85)',
        unitsBadgeBg: '#E7E7E7',
        propertyTagBg: '#F3F3F3',

        // Link/Action Colors
        linkBlue: '#5095EF',

        // Next Step / Action Card Colors
        appointmentIconBg: '#2d1f18',
        appointmentIconTint: '#ff7d47',
        appointmentBadgeBg: UI_PRIMITIVES.redLightest2,
        appointmentBadgeText: UI_PRIMITIVES.redLight,
        reserveIconBg: '#1b2d1e',
        reserveIconTint: '#81c784',
        skeletonBaseDark: '#2A2A2A',
        skeletonHighlightDark: '#3A3A3A',
        reservationPendingBg: UI_PRIMITIVES.orangePale60,
        reservationPendingText: UI_PRIMITIVES.orangeDeep,
        reservationInProgressBg: UI_PRIMITIVES.yellowLightest,
        reservationInProgressText: UI_PRIMITIVES.goldInProgress,
        reservationListingConfirmedBg: UI_PRIMITIVES.greenLighter,
        reservationListingConfirmedText: UI_PRIMITIVES.greenIskan,
        reservationConfirmedText: UI_PRIMITIVES.greenMedium,
        adhaUnitStatusText: UI_PRIMITIVES.adhaUnitStatusText,
        adhaUnitStatusBg: UI_PRIMITIVES.adhaUnitStatusBg,

        iskanLoanText: '#81c784',
        remainingBalanceText: UI_PRIMITIVES.redDefault,
        importantNoteBg: '#2d1f18',
        importantNoteBorder: '#5c3d2e',
        importantNoteTitle: '#ff7d47',
        importantNoteBody: '#c5c6cc',
        confirmNextBorder: UI_PRIMITIVES.greyCCCC,
        successNoteBg: '#1b2d1e',
        successNoteBorder: '#81c784',
        successNoteTitle: '#81c784',

        // Modal Backdrop Overlay
        modalBackdrop: 'rgba(0, 0, 0, 0.6)',

        // Bloom Experience hero landing screen (same as light — fixed brand colors)
        bloomExperienceLogoText: UI_PRIMITIVES.bloomExperienceCream,
        bloomExperienceLabelText: UI_PRIMITIVES.bloomExperienceGold,
        bloomExperienceTitleText: UI_PRIMITIVES.bloomExperienceIvory,
    },
};

// Merged once and cached — `getColors('dark')` used to spread a fresh object
// on every call, which is cheap by itself but means every component that
// takes `colors` as a prop (most screens do) got a new reference on every
// render in dark mode, silently defeating any `React.memo` on those
// components regardless of their other props being stable.
let cachedDarkColors: UIColorTokens | null = null;

export const getColors = (theme: ThemeName = 'light'): UIColorTokens => {
    const light = UI_COLORS.light as UIColorTokens;
    if (theme === 'dark') {
        cachedDarkColors ??= { ...light, ...(UI_COLORS.dark as Partial<UIColorTokens>) };
        return cachedDarkColors;
    }
    return light;
};


